const initializeTableOfContents = (toc) => {
  if (!(toc instanceof HTMLElement) || toc.dataset.initialized === 'true') return;
  const article = toc.closest('.br-article');
  const list = toc.querySelector('[data-br-article-toc-list]');
  const toggle = toc.querySelector('[data-br-article-toc-toggle]');
  const content = toc.querySelector('[data-br-article-toc-content]');
  const currentLabel = toc.querySelector('[data-br-article-toc-current]');
  const headings = Array.from(article?.querySelectorAll('.br-article__body h2') || []);
  if (!(list instanceof HTMLOListElement) || !(toggle instanceof HTMLButtonElement) ||
      !(content instanceof HTMLElement) || headings.length < 2) return;

  const lifecycle = new AbortController();
  const options = { signal: lifecycle.signal };
  const usedIds = new Set(Array.from(document.querySelectorAll('[id]'), (element) => element.id).filter(Boolean));
  const headingLinks = new Map();
  headings.forEach((heading) => {
    if (!heading.id) {
      const baseSlug = heading.textContent?.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().trim().replace(/[^\p{Letter}\p{Number}]+/gu, '-').replace(/^-+|-+$/g, '') || 'section';
      let slug = baseSlug;
      let suffix = 2;
      while (usedIds.has(slug)) slug = `${baseSlug}-${suffix++}`;
      heading.id = slug;
      usedIds.add(slug);
    }
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${encodeURIComponent(heading.id)}`;
    link.textContent = heading.textContent?.trim() || heading.id;
    item.append(link);
    list.append(item);
    headingLinks.set(heading, link);
  });

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let activeHeading = null;
  let viewportFrame = 0;
  let navigationFrame = 0;
  const setActiveHeading = (heading) => {
    if (activeHeading === heading) return;
    activeHeading = heading;
    headingLinks.forEach((link, linkedHeading) => {
      if (linkedHeading === heading) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    if (currentLabel) currentLabel.textContent = heading?.textContent?.trim() || 'Choose a section';
  };
  const fitMobilePanel = () => {
    if (!mobileQuery.matches || content.hidden) return;
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const available = Math.max(44, Math.floor(viewportBottom - toc.getBoundingClientRect().bottom - 24));
    toc.style.setProperty('--br-toc-panel-height', `${available}px`);
  };
  const setExpanded = (expanded) => {
    toggle.setAttribute('aria-expanded', String(expanded));
    content.hidden = !expanded;
    fitMobilePanel();
  };
  const setActiveFromViewport = () => {
    viewportFrame = 0;
    if (!article.isConnected) return;
    fitMobilePanel();
    if (mobileQuery.matches && toggle.getAttribute('aria-expanded') === 'true') return;
    const readingLine = Math.min(window.innerHeight * 0.3, 300);
    const passed = headings.filter((heading) => heading.getBoundingClientRect().top <= readingLine).at(-1);
    setActiveHeading(passed || null);
  };
  const scheduleViewportUpdate = () => {
    if (!viewportFrame) viewportFrame = window.requestAnimationFrame(setActiveFromViewport);
  };
  const closeMobile = (restoreFocus = false) => {
    if (!mobileQuery.matches || toggle.getAttribute('aria-expanded') !== 'true') return;
    setExpanded(false);
    if (restoreFocus) toggle.focus({ preventScroll: true });
  };

  setExpanded(!mobileQuery.matches);
  toggle.addEventListener('click', () => setExpanded(toggle.getAttribute('aria-expanded') !== 'true'), options);
  toc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMobile(true);
      event.stopPropagation();
    }
  }, options);
  document.addEventListener('pointerdown', (event) => {
    if (!toc.contains(event.target)) closeMobile();
  }, options);
  headingLinks.forEach((link, heading) => {
    link.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      window.cancelAnimationFrame(navigationFrame);
      setActiveHeading(heading);
      if (mobileQuery.matches) setExpanded(false);
      if (window.location.hash !== link.hash) {
        window.history.pushState(window.history.state, '', link.href);
      }
      // Scroll after the collapsed menu has changed the page layout. An instant
      // mobile jump also cancels any theme-level smooth scroll already running.
      navigationFrame = window.requestAnimationFrame(() => {
        navigationFrame = 0;
        if (!heading.isConnected || lifecycle.signal.aborted) return;
        const addedTabindex = !heading.hasAttribute('tabindex');
        if (addedTabindex) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        if (addedTabindex) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
        heading.scrollIntoView({ behavior: mobileQuery.matches ? 'instant' : 'auto', block: 'start' });
        scheduleViewportUpdate();
      });
    }, options);
  });
  mobileQuery.addEventListener('change', () => {
    setExpanded(!mobileQuery.matches);
    scheduleViewportUpdate();
  }, options);
  window.addEventListener('scroll', scheduleViewportUpdate, { ...options, passive: true });
  window.addEventListener('resize', scheduleViewportUpdate, options);
  window.visualViewport?.addEventListener('resize', scheduleViewportUpdate, options);
  window.visualViewport?.addEventListener('scroll', scheduleViewportUpdate, { ...options, passive: true });
  window.addEventListener('hashchange', scheduleViewportUpdate, options);
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.contains(article)) {
      lifecycle.abort();
      window.cancelAnimationFrame(viewportFrame);
      window.cancelAnimationFrame(navigationFrame);
    }
  }, options);
  const hashHeading = headings.find((heading) => `#${encodeURIComponent(heading.id)}` === window.location.hash);
  if (hashHeading) setActiveHeading(hashHeading);
  toc.dataset.initialized = 'true';
  toc.hidden = false;
  scheduleViewportUpdate();
};
const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } finally {
    textarea.remove();
  }

  return copied;
};

const initializeShare = (share) => {
  if (!(share instanceof HTMLElement) || share.dataset.initialized === 'true') return;

  const nativeButton = share.querySelector('[data-br-share-native]');
  const copyButton = share.querySelector('[data-br-share-copy]');
  const status = share.querySelector('[data-br-share-status]');
  const title = share.dataset.shareTitle || document.title;
  const url = share.dataset.shareUrl || window.location.href;

  if (!(copyButton instanceof HTMLButtonElement) || !(status instanceof HTMLElement)) return;

  if (nativeButton instanceof HTMLButtonElement && typeof navigator.share === 'function') {
    nativeButton.hidden = false;
    nativeButton.addEventListener('click', async () => {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        if (!(error instanceof DOMException) || error.name !== 'AbortError') {
          status.textContent = 'Unable to share. You can copy the link instead.';
        }
      }
    });
  }

  copyButton.addEventListener('click', async () => {
    try {
      const copied = await copyToClipboard(url);
      status.textContent = copied ? 'Link copied' : 'Unable to copy. Please copy the address from your browser.';
    } catch {
      status.textContent = 'Unable to copy. Please copy the address from your browser.';
    }
  });

  share.dataset.initialized = 'true';
};

const initializeArticleFeatures = (root = document) => {
  root.querySelectorAll?.('[data-br-article-toc]').forEach(initializeTableOfContents);
  root.querySelectorAll?.('[data-br-article-share]').forEach(initializeShare);
};

initializeArticleFeatures();

document.addEventListener('shopify:section:load', (event) => {
  initializeArticleFeatures(event.target);
});
